import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAxios } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import styled, { keyframes, useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H1 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { mixpanel } from '@/mixpanel'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { formatConstraintHint, type SelectedFile, toSelectedFile } from '../../utils/file-upload-selection.utils'

import type { FileUploadRequestTask } from '../../types/task.types'

type Props = {
  task: FileUploadRequestTask
  onClose: () => void
  onSuccess: () => void
}

export const FileUploadSelection = ({ task, onClose, onSuccess }: Props) => {
  const theme = useTheme()
  const axios = useAxios()
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)

  const { allowedTypes = [], minFiles = 1, maxFiles = 1 } = task.meta ?? {}
  const hasValidationErrors = selectedFiles.some((f) => f.error !== null)
  const hasUploadErrors = selectedFiles.some((f) => f.uploadError !== null)
  const hasErrors = hasValidationErrors || hasUploadErrors
  const errorCount = selectedFiles.filter((f) => f.error !== null || f.uploadError !== null).length
  const canAddMore = selectedFiles.length < maxFiles
  const canSubmit = !hasValidationErrors && selectedFiles.length >= minFiles && !submitting

  const constraintHint = formatConstraintHint(allowedTypes)

  // Revoke all object URLs on unmount
  const selectedFilesRef = useRef(selectedFiles)
  selectedFilesRef.current = selectedFiles
  useEffect(() => {
    return () => {
      selectedFilesRef.current.forEach((sf) => {
        if (sf.previewUrl) URL.revokeObjectURL(sf.previewUrl)
      })
    }
  }, [])

  const addFiles = (files: FileList) => {
    const toAdd = [...files].slice(0, maxFiles - selectedFiles.length)
    const selected = toAdd.map((file) => toSelectedFile(file, allowedTypes))
    selected.forEach((sf) => {
      mixpanel.track('file_upload_request_file_added', {
        file_type: sf.file.type.split('/')[1] ?? 'unknown',
        is_mandate: isLoggedInForMandatee,
      })
      if (sf.error) {
        mixpanel.track('file_upload_request_failed', {
          error_type: 'validation',
          file_count: 1,
          is_mandate: isLoggedInForMandatee,
        })
      }
    })
    setSelectedFiles((previous) => [...previous, ...selected])
  }

  const removeFile = (id: string) => {
    setSelectedFiles((previous) => {
      const removed = previous.find((f) => f.id === id)
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
      return previous.filter((f) => f.id !== id)
    })
  }

  const handleFileInputChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    if (event_.target.files?.length) {
      addFiles(event_.target.files)
      // Reset so selecting the same file again triggers onChange
      event_.target.value = ''
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setCompleteError(null)

    // Snapshot the files we need to upload (skip already-succeeded ones on retry)
    const pending = selectedFiles.filter((sf) => !sf.fileId)

    // Mark pending files as uploading so tiles show their spinner
    setSelectedFiles((previous) =>
      previous.map((sf) => (pending.some((p) => p.id === sf.id) ? { ...sf, uploading: true, uploadError: null } : sf)),
    )

    // Upload all pending files in parallel, updating each tile as it resolves
    const results = await Promise.all(
      pending.map(async (sf) => {
        try {
          const formData = new FormData()
          formData.append('file', sf.file)
          formData.append('fileUploadRequestId', String(task.careplanActivityId))
          formData.append('mimeType', sf.file.type)
          const { data } = await axios.post<{ fileId: string }>('/file-upload', formData)
          setSelectedFiles((previous) =>
            previous.map((f) =>
              f.id === sf.id
                ? {
                    ...f,
                    uploading: false,
                    fileId: data.fileId,
                    uploadError: null,
                  }
                : f,
            ),
          )
          return { id: sf.id, fileId: data.fileId, error: null }
        } catch (error_) {
          mixpanel.track('file_upload_request_failed', {
            error_type: error_ instanceof AxiosError && error_.response ? 'server' : 'network',
            file_count: 1,
            is_mandate: isLoggedInForMandatee,
          })
          const error = i18n._(globalDefinitions.task.FileUploadRequest.selection.error.upload_failed)
          setSelectedFiles((previous) =>
            previous.map((f) => (f.id === sf.id ? { ...f, uploading: false, uploadError: error } : f)),
          )
          return { id: sf.id, fileId: null, error }
        }
      }),
    )

    if (results.some((r) => r.error !== null)) {
      setSubmitting(false)
      return
    }

    // All uploads succeeded — collect fileIds from already-uploaded + just-uploaded
    const fileIds = selectedFiles.map((sf) => {
      const result = results.find((r) => r.id === sf.id)
      return result?.fileId ?? sf.fileId!
    })

    try {
      await axios.patch(`/careplan-activity/${task.careplanActivityId}`, {
        status: 'completed',
        meta: { fileIds },
      })
      mixpanel.track('file_upload_request_submitted', {
        file_count: fileIds.length,
        is_mandate: isLoggedInForMandatee,
      })
      onSuccess()
    } catch {
      mixpanel.track('file_upload_request_failed', {
        error_type: 'server',
        file_count: fileIds.length,
        is_mandate: isLoggedInForMandatee,
      })
      setCompleteError(i18n._(globalDefinitions.task.FileUploadRequest.selection.error.complete_failed))
    }

    setSubmitting(false)
  }

  return (
    <StContainer gap={theme.UI.Spacing.Space8}>
      <StHeader>
        <H1 noMargin textAlign='center'>
          {i18n._(globalDefinitions.task.FileUploadRequest.selection.title)}
        </H1>
        <StCloseButton type='button' onClick={onClose} aria-label={i18n._(globalDefinitions.close)}>
          <FontAwesomeIcon icon={['fal', 'times']} fontSize={16} color={theme.Colors.Theme.Primary} />
        </StCloseButton>
      </StHeader>

      {/* File grid */}
      <StScrollArea>
        <StGrid>
          {selectedFiles.map((sf) => (
            <StFileCell key={sf.id}>
              <StThumbnail hasError={!!(sf.error || sf.uploadError)}>
                {sf.previewUrl ? (
                  <StPreviewImage src={sf.previewUrl} alt={sf.file.name} />
                ) : (
                  <FontAwesomeIcon
                    icon={sf.file.type === 'application/pdf' ? ['fal', 'file-pdf'] : ['fal', 'file']}
                    size='2x'
                    color={theme.Colors.Neutrals.Darker}
                  />
                )}
                {sf.uploading && (
                  <StUploadingOverlay>
                    <StSpinRing />
                  </StUploadingOverlay>
                )}
                {sf.fileId && !sf.uploading && (
                  <StSuccessBadge>
                    <FontAwesomeIcon icon={['fal', 'check']} size='xs' color='#fff' />
                  </StSuccessBadge>
                )}
                {!sf.uploading && !sf.fileId && (
                  <StRemoveButton
                    hasError={!!(sf.error || sf.uploadError)}
                    type='button'
                    onClick={() => removeFile(sf.id)}
                    aria-label={i18n._(globalDefinitions.remove)}
                  >
                    <FontAwesomeIcon icon={['fal', 'times']} size='xs' color='#fff' />
                  </StRemoveButton>
                )}
              </StThumbnail>
              {(sf.error || sf.uploadError) && (
                <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
                  {sf.error ?? sf.uploadError}
                </Span>
              )}
            </StFileCell>
          ))}

          {/* Add slot */}
          {canAddMore && (
            <StAddSlot
              type='button'
              onClick={() => fileInputRef.current?.click()}
              aria-label={i18n._(globalDefinitions.task.FileUploadRequest.selection.add)}
            >
              <StAddIcon icon={['fal', 'plus']} size='lg' />
            </StAddSlot>
          )}
        </StGrid>
      </StScrollArea>

      {/* Footer */}
      <FlexColumn gap={theme.UI.Spacing.Space2} style={{ paddingTop: theme.UI.SpacingPx.Space8 }}>
        {/* Error banner replaces constraint hint when errors are present */}
        {hasErrors || completeError ? (
          <StErrorBanner>
            <StErrorIcon>
              <FontAwesomeIcon icon={['far', 'triangle-exclamation']} color={theme.Colors.Status.Error} fontSize={20} />
            </StErrorIcon>
            <StErrorText>
              {completeError ? (
                <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
                  {completeError}
                </Span>
              ) : (
                <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
                  <Span type='BodySmallBold' color={theme.Colors.Status.Error}>
                    {errorCount === 1
                      ? i18n._(globalDefinitions.task.FileUploadRequest.selection.error.banner_one)
                      : i18n._(globalDefinitions.task.FileUploadRequest.selection.error.banner_many, {
                          count: errorCount,
                        })}
                  </Span>{' '}
                  {errorCount === 1
                    ? i18n._(globalDefinitions.task.FileUploadRequest.selection.error.banner_one_detail)
                    : i18n._(globalDefinitions.task.FileUploadRequest.selection.error.banner_many_detail)}
                </Span>
              )}
            </StErrorText>
          </StErrorBanner>
        ) : constraintHint ? (
          <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Dark} style={{ textAlign: 'center' }}>
            {constraintHint}
          </Span>
        ) : null}
        <Button
          variant='Primary'
          disabled={!canSubmit}
          onClick={handleSubmit}
          style={{ marginTop: theme.UI.SpacingPx.Space2 }}
        >
          {submitting
            ? i18n._(globalDefinitions.task.FileUploadRequest.selection.uploading)
            : hasUploadErrors
              ? i18n._(globalDefinitions.task.FileUploadRequest.selection.retry)
              : i18n._(globalDefinitions.task.FileUploadRequest.selection.submit)}
        </Button>
        <Button variant='Tertiary' onClick={onClose}>
          {i18n._(globalDefinitions.cancel)}
        </Button>
      </FlexColumn>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type='file'
        accept={allowedTypes.map((t) => t.mimeType).join(',') || undefined}
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </StContainer>
  )
}

const StContainer = styled(FlexColumn)`
  height: 100%;
  min-height: 0;
`

const StScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`

const StGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: start;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
  padding-top: 12px;

  @media ${breakpoints.toS} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StFileCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
`

const StThumbnail = styled.div<{ hasError: boolean }>`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid
    ${({ theme, hasError }) => (hasError ? theme.Colors.Status.Error : 'transparent')};
`

const StPreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const StSuccessBadge = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 24px;
  height: 24px;
  margin: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.Colors.Status.Success};
`

const StRemoveButton = styled.button<{ hasError: boolean }>`
  position: absolute;
  top: 0px;
  right: 0px;
  width: 24px;
  height: 24px;
  margin: 8px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, hasError }) => (hasError ? theme.Colors.Status.Error : theme.Colors.Theme.Primary)};
  padding: 0;
`

const StAddIcon = styled(FontAwesomeIcon)`
  width: 20px;
  height: 20px;
  padding: 26px;
  background-color: ${({ theme }) => theme.Components.Button.Secondary.ActiveFill};
  border-radius: 50%;
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
  transition: all 250ms ease-out;
`

const StAddSlot = styled.button`
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  border: 2px dashed ${({ theme }) => theme.Colors.Neutrals.Light};
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 250ms ease-out;

  &:hover {
    border-color: ${({ theme }) => theme.Components.Button.Primary.Fill};

    ${StAddIcon} {
      background-color: ${({ theme }) => theme.Components.Button.Primary.Fill};
      color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
    }
  }
`

const StUploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const StSpinRing = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: ${spin} 0.75s linear infinite;
`

const StHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StCloseButton = styled.button`
  position: absolute;
  right: 0;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space2};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StErrorBanner = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
  align-items: flex-start;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space5};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.Colors.Status.Error};
  /* Solid error-background tint from the design tokens; no theme token exists
     for it yet, so it's set literally here. */
  background-color: #ffe8e8;
`

const StErrorText = styled.div`
  flex: 1;
  min-width: 0;
`

// Nudge the icon down so it aligns with the first text line instead of
// floating above it (matches the design's 2px top offset on the icon).
const StErrorIcon = styled.div`
  display: flex;
  align-items: center;
  padding-top: 2px;
`
